'use client';

/**
 * OutputsSection — downstream-artifact table that lives under the
 * OUTPUTS heading on the ArchitectureTab. Each row is a single artifact
 * produced AFTER scoring. Status dot is derived from mtime age, computed
 * server-side and shipped on the architecture-state payload.
 */

import type { OutputArtifact } from '@/lib/architecture-state';

const DOT: Record<OutputArtifact['status'], string> = {
  ok: 'bg-emerald-400 shadow-[0_0_6px_rgba(74,222,128,0.55)]',
  warn: 'bg-amber-300 shadow-[0_0_6px_rgba(252,211,77,0.5)]',
  fail: 'bg-red-400 shadow-[0_0_6px_rgba(248,113,113,0.55)]',
  na: 'bg-zinc-700',
};

const TONE: Record<OutputArtifact['status'], string> = {
  ok: 'text-white/80',
  warn: 'text-amber-300',
  fail: 'text-red-300',
  na: 'text-white/40',
};

function fmtBytes(b: number | null): string {
  if (b == null) return '—';
  if (b >= 1_000_000) return `${(b / 1_000_000).toFixed(1)}M`;
  if (b >= 1_000) return `${(b / 1_000).toFixed(0)}K`;
  return `${b}B`;
}

function fmtMtime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function fmtAge(ageMs: number | null): string {
  if (ageMs == null) return '—';
  const h = ageMs / 3600_000;
  if (h < 1) {
    const m = Math.max(0, Math.round(ageMs / 60_000));
    return `${m}m`;
  }
  if (h < 48) return `${h.toFixed(1)}h`;
  return `${Math.round(h / 24)}d`;
}

interface Props {
  outputs: OutputArtifact[];
}

export function OutputsSection({ outputs }: Props): React.JSX.Element {
  return (
    <div className="rounded-md border border-zinc-800 bg-[#0a0e14] p-4">
      <div className="flex items-baseline justify-between">
        <div className="text-[12px] uppercase tracking-wider text-[#c9a84c]">
          Downstream artifacts
        </div>
        <div className="text-[10px] text-white/40">
          {outputs.length} files · everything downstream of scoring
        </div>
      </div>

      {outputs.length === 0 ? (
        <div className="mt-3 text-[11px] text-white/40">No outputs registered.</div>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead>
              <tr className="text-[9px] uppercase tracking-wider text-white/40">
                <th className="text-left font-normal pb-2 pl-1 pr-3">Artifact</th>
                <th className="text-right font-normal pb-2 pr-3 w-[140px]">Last write</th>
                <th className="text-right font-normal pb-2 pr-3 w-[70px]">Age</th>
                <th className="text-right font-normal pb-2 pr-3 w-[70px]">Size</th>
                <th className="text-center font-normal pb-2 pr-1 w-[40px]">·</th>
              </tr>
            </thead>
            <tbody>
              {outputs.map((o) => (
                <tr
                  key={o.path}
                  className="border-t border-zinc-900 hover:bg-zinc-900/30"
                  title={o.path}
                >
                  <td className="py-1.5 pl-1 pr-3">
                    <div className="text-[11px] text-white/85">{o.label}</div>
                    <div className="text-[9px] text-white/40 font-mono">{o.path}</div>
                  </td>
                  <td className={`py-1.5 pr-3 text-right tabular-nums ${TONE[o.status]}`}>
                    {fmtMtime(o.mtime)}
                  </td>
                  <td className={`py-1.5 pr-3 text-right tabular-nums ${TONE[o.status]}`}>
                    {fmtAge(o.ageMs)}
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums text-white/60">
                    {fmtBytes(o.bytes)}
                  </td>
                  <td className="py-1.5 pr-1 text-center">
                    <span
                      className={`inline-block w-2 h-2 rounded-full ${DOT[o.status]}`}
                      aria-hidden
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
